/* eslint-disable jsx-a11y/no-autofocus */

import { FC, useState } from 'react';
import { VStack } from '@chakra-ui/react';
import { isEmpty } from 'ramda';

import * as Form from 'components/_core/form';
import Heading from 'components/_core/Heading';
import Text from 'components/_core/Text';
import BackButton from 'components/BackButton';
import { StepProps } from 'components/Stepper/types';
import * as StepperLayout from 'layouts/StepperLayout';

import commonLocales from '../../common/locales';
import * as CommonStyles from '../../common/styles';
import NextButton from '../../components/NextButton';
import { useMandateCreationFlowContext } from '../../hooks/useMandateCreationFlowContext';
import locales from './locales';
import { Errors } from './types';
import { validateForm } from './utils/formValidation';

const StepMandateInfoForm: FC<StepProps> = ({ goBack, goNext }) => {
  const { setSharedState, sharedState } = useMandateCreationFlowContext();
  const { mandate } = sharedState;

  const [creditorIdentifier, setCreditorIdentifier] = useState(
    mandate.creditorIdentifier,
  );
  const [creditorName, setCreditorName] = useState(mandate.creditorName);
  const [label, setLabel] = useState(mandate.label);
  const [uniqueMandateReference, setUniqueMandateReference] = useState(
    mandate.uniqueMandateReference,
  );

  const [validationErrors, setValidationErrors] = useState<Errors>({});

  const onSubmit = () => {
    const sanitizedCreditorIdentifier = creditorIdentifier.trim();
    const sanitizedCreditorName = creditorName.trim();
    const sanitizedLabel = label.trim();
    const sanitizedUniqueMandateReference = uniqueMandateReference.trim();
    setCreditorIdentifier(sanitizedCreditorIdentifier);
    setCreditorName(sanitizedCreditorName);
    setLabel(sanitizedLabel);
    setUniqueMandateReference(sanitizedUniqueMandateReference);

    const errors = validateForm({
      creditorIdentifier: sanitizedCreditorIdentifier,
      creditorName: sanitizedCreditorName,
      uniqueMandateReference: sanitizedUniqueMandateReference,
    });
    setValidationErrors(errors);
    if (!isEmpty(Object.keys(errors))) {
      return;
    }

    setSharedState({
      ...sharedState,
      mandate: {
        ...mandate,
        creditorIdentifier: sanitizedCreditorIdentifier,
        creditorName: sanitizedCreditorName,
        label: sanitizedLabel,
        uniqueMandateReference: sanitizedUniqueMandateReference,
      },
    });
    goNext();
  };

  const clearErrorState = (errorKey: keyof Errors) => {
    if (!validationErrors[errorKey]) {
      return;
    }
    const newErrors = { ...validationErrors };
    delete newErrors[errorKey];
    setValidationErrors(newErrors);
  };

  const canClickOnNext =
    creditorIdentifier &&
    creditorName &&
    uniqueMandateReference &&
    isEmpty(Object.keys(validationErrors));

  return (
    <StepperLayout.Root>
      <StepperLayout.Body>
        <CommonStyles.FormWrapper onValidSubmit={onSubmit}>
          <CommonStyles.HeaderContainer>
            <BackButton onClick={goBack} />
          </CommonStyles.HeaderContainer>

          <StepperLayout.BodyScrollableSection>
            <Heading marginBottom="space-8" size="lg">
              {locales.title}
            </Heading>
            <Text marginBottom="space-40">{locales.subTitle}</Text>

            <VStack align="stretch" spacing="space-24">
              <Form.Field
                error={validationErrors.creditorName}
                label={locales.form.creditorName.label}
              >
                <Form.Input
                  autoFocus={isEmpty(creditorName)}
                  onChange={(event) => {
                    clearErrorState('creditorName');
                    setCreditorName(event.target.value);
                  }}
                  placeholder={locales.form.creditorName.placeholder}
                  value={creditorName}
                />
              </Form.Field>

              <Form.Field
                hint={locales.form.label.hint}
                label={locales.form.label.label}
              >
                <Form.Input
                  onChange={(event) => setLabel(event.target.value)}
                  placeholder={locales.form.label.placeholder}
                  value={label}
                />
              </Form.Field>

              <Form.Field
                error={validationErrors.uniqueMandateReference}
                hint={locales.form.uniqueMandateReference.helpText}
                label={locales.form.uniqueMandateReference.label}
              >
                <Form.Input
                  onChange={(event) => {
                    clearErrorState('uniqueMandateReference');
                    setUniqueMandateReference(event.target.value);
                  }}
                  placeholder={locales.form.uniqueMandateReference.placeholder}
                  value={uniqueMandateReference}
                />
              </Form.Field>

              <Form.Field
                error={validationErrors.creditorIdentifier}
                label={locales.form.creditorIdentifier.label}
              >
                <Form.Input
                  onChange={(event) => {
                    clearErrorState('creditorIdentifier');
                    setCreditorIdentifier(event.target.value);
                  }}
                  placeholder={locales.form.creditorIdentifier.placeholder}
                  value={creditorIdentifier}
                />
              </Form.Field>
            </VStack>
          </StepperLayout.BodyScrollableSection>

          <CommonStyles.FooterContainer>
            <NextButton isDisabled={!canClickOnNext} type="submit">
              {commonLocales.nextButton}
            </NextButton>
          </CommonStyles.FooterContainer>
        </CommonStyles.FormWrapper>
      </StepperLayout.Body>
    </StepperLayout.Root>
  );
};

export default StepMandateInfoForm;
